import { createContext, useContext } from 'react';
import { AppTheme, DEFAULT_THEMES } from '../models/types';

export interface ThemeContextType {
  currentTheme: AppTheme;
  themes: AppTheme[];
  setTheme: (theme: AppTheme) => void;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const getStoredTheme = (): AppTheme => {
  try {
    const stored = localStorage.getItem('medmgmt-ai-theme');
    if (stored) {
      const parsed = JSON.parse(stored);
      const theme = DEFAULT_THEMES.find(t => t.name === parsed.name);
      if (theme) return theme;
    }
  } catch (error) {
    console.warn('Failed to load stored theme:', error);
  }
  
  // Default to system preference
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return DEFAULT_THEMES.find(t => t.isDark === prefersDark) || DEFAULT_THEMES[0];
};

export const storeTheme = (theme: AppTheme): void => {
  try {
    localStorage.setItem('medmgmt-ai-theme', JSON.stringify(theme));
  } catch (error) {
    console.warn('Failed to store theme:', error);
  }
};

// Utility function to generate CSS custom properties for themes
export const getThemeCSSProps = (theme: AppTheme): Record<string, string> => {
  if (theme.isDark) {
    return {
      '--color-background': '#1a1a1a',
      '--color-surface': '#2d2d2d',
      '--color-text-primary': '#ffffff',
      '--color-text-secondary': '#cccccc',
      '--color-border': '#404040',
      '--color-accent': '#0078d4',
      '--color-accent-hover': '#106ebe',
    };
  } else {
    return {
      '--color-background': '#ffffff',
      '--color-surface': '#f8f9fa',
      '--color-text-primary': '#323130',
      '--color-text-secondary': '#605e5c',
      '--color-border': '#e1dfdd',
      '--color-accent': '#0078d4',
      '--color-accent-hover': '#106ebe',
    };
  }
};
